import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';

const TRUELOG_CYAN = '#0E9ED5';
const TRUELOG_BLUE = '#385CF2';
const TRUELOG_ORANGE = '#F97316'; // planned office (2027 opening)

// Same palette as the homepage GlobalMap: every country in a region is served,
// so there is one "served" fill, brightened for region hover/selection.
const SERVED_FILL = '#0b3a5c';
const SERVED_STROKE = '#1f6f9c';
const REGION_HOVER_FILL = '#0e5a8a';
const REGION_SELECTED_FILL = '#0f6aa3';
const UNSERVED_FILL = '#111c2e';
const HIDDEN_GEOGRAPHIES = new Set(['Antarctica', 'Fr. S. Antarctic Lands']);

export interface Region {
  id: string;
  name: string;
  color: string;
  activeColor: string;
  hoverColor: string;
  description: string;
  countries: CountryInfo[];
  stats: {
    markets: number;
    partners: number;
  };
  services: string[];
}

export interface CountryInfo {
  name: string;
  code: string;
  hasOffice?: boolean;
  isPartner?: boolean; // Active service operations
  services?: string[];
}

// Country to region mapping
const COUNTRY_REGIONS: { [key: string]: string } = {
  // Europe
  'Albania': 'europe', 'Andorra': 'europe', 'Austria': 'europe', 'Belgium': 'europe',
  'Bosnia and Herz.': 'europe', 'Bulgaria': 'europe', 'Croatia': 'europe',
  'Czech Rep.': 'europe', 'Czechia': 'europe', 'Denmark': 'europe', 'Estonia': 'europe', 'Finland': 'europe',
  'France': 'europe', 'Germany': 'europe', 'Greece': 'europe', 'Hungary': 'europe',
  'Iceland': 'europe', 'Ireland': 'europe', 'Italy': 'europe',
  'Latvia': 'europe', 'Lithuania': 'europe', 'Luxembourg': 'europe',
  'Malta': 'europe', 'Monaco': 'europe',
  'Montenegro': 'europe', 'Netherlands': 'europe', 'Norway': 'europe', 'Poland': 'europe',
  'Portugal': 'europe', 'Romania': 'europe', 'San Marino': 'europe',
  'Scotland': 'europe', 'Serbia': 'europe', 'Slovakia': 'europe', 'Slovenia': 'europe',
  'Spain': 'europe', 'Sweden': 'europe', 'Switzerland': 'europe',
  'United Kingdom': 'europe', 'Vatican': 'europe',
  // Eastern Europe / former CIS — were missing
  'Russia': 'europe', 'Belarus': 'europe', 'Ukraine': 'europe', 'Moldova': 'europe',
  'Macedonia': 'europe', 'Kosovo': 'europe',
  'Greenland': 'europe',

  // APAC
  'Afghanistan': 'apac', 'Australia': 'apac', 'Bangladesh': 'apac', 'Bhutan': 'apac',
  'Brunei': 'apac', 'Cambodia': 'apac', 'China': 'apac', 'India': 'apac',
  'Indonesia': 'apac', 'Japan': 'apac', 'Kazakhstan': 'apac', 'Kyrgyzstan': 'apac',
  'Malaysia': 'apac', 'Maldives': 'apac', 'Mongolia': 'apac', 'Myanmar': 'apac',
  'Nepal': 'apac', 'New Zealand': 'apac', 'Oman': 'apac', 'Pakistan': 'apac',
  'Palau': 'apac', 'Papua New Guinea': 'apac', 'Philippines': 'apac', 'Singapore': 'apac',
  'Solomon Is.': 'apac', 'South Korea': 'apac', 'N. Korea': 'apac', 'Sri Lanka': 'apac',
  'Taiwan': 'apac', 'Tajikistan': 'apac', 'Thailand': 'apac', 'Timor-Leste': 'apac',
  'Turkmenistan': 'apac', 'Uzbekistan': 'apac', 'Vanuatu': 'apac', 'Vietnam': 'apac',
  'Hong Kong': 'apac', 'Fiji': 'apac', 'Laos': 'apac',
  'Azerbaijan': 'apac', 'Armenia': 'apac', 'Georgia': 'apac',

  // Africa
  'Algeria': 'africa', 'Angola': 'africa', 'Benin': 'africa', 'Botswana': 'africa',
  'Burkina Faso': 'africa', 'Cameroon': 'africa', 'Chad': 'africa', 'Congo': 'africa',
  'Dem. Rep. Congo': 'africa', 'Egypt': 'africa', 'Eritrea': 'africa', 'Ethiopia': 'africa',
  'Gabon': 'africa', 'Ghana': 'africa', 'Guinea': 'africa',
  "Côte d'Ivoire": 'africa', 'Kenya': 'africa', 'Lesotho': 'africa', 'Liberia': 'africa',
  'Madagascar': 'africa', 'Malawi': 'africa', 'Mali': 'africa', 'Mauritania': 'africa',
  'Morocco': 'africa', 'Mozambique': 'africa', 'Namibia': 'africa',
  'Niger': 'africa', 'Nigeria': 'africa', 'Senegal': 'africa',
  'Sierra Leone': 'africa', 'Somalia': 'africa',
  'South Africa': 'africa', 'Sudan': 'africa', 'S. Sudan': 'africa',
  'Swaziland': 'africa', 'Tanzania': 'africa', 'Togo': 'africa',
  'Tunisia': 'africa', 'Uganda': 'africa', 'W. Sahara': 'africa', 'Zimbabwe': 'africa',

  // Americas
  'Argentina': 'americas', 'Bolivia': 'americas', 'Brazil': 'americas', 'Canada': 'americas',
  'Chile': 'americas', 'Colombia': 'americas', 'Costa Rica': 'americas',
  'Cuba': 'americas', 'Dominican Rep.': 'americas', 'Ecuador': 'americas',
  'Falkland Is.': 'americas', 'French Guiana': 'americas', 'Guatemala': 'americas',
  'Guyana': 'americas', 'Haiti': 'americas', 'Honduras': 'americas',
  'Jamaica': 'americas', 'Mexico': 'americas', 'Nicaragua': 'americas',
  'Panama': 'americas', 'Peru': 'americas', 'Suriname': 'americas',
  'Uruguay': 'americas', 'United States': 'americas', 'United States of America': 'americas',
  'Antigua and Barb.': 'americas', 'Bahamas': 'americas', 'Barbados': 'americas',
  'Belize': 'americas', 'Grenada': 'americas',
  'St. Kitts and Nevis': 'americas', 'Saint Lucia': 'americas',
  'Puerto Rico': 'americas', 'Alaska': 'americas',
  'El Salvador': 'americas', 'Paraguay': 'americas', 'Trinidad and Tobago': 'americas',
  'Venezuela': 'americas',

  // Middle East
  'Bahrain': 'middleEast', 'Cyprus': 'middleEast', 'Iran': 'middleEast', 'Iraq': 'middleEast',
  'Israel': 'middleEast', 'Jordan': 'middleEast', 'Kuwait': 'middleEast',
  'Lebanon': 'middleEast', 'Palestine': 'middleEast', 'Qatar': 'middleEast',
  'Saudi Arabia': 'middleEast', 'Syria': 'middleEast', 'Turkey': 'middleEast',
  'United Arab Emirates': 'middleEast',
};

export const REGIONS: Region[] = [
  {
    id: 'europe',
    name: 'Europe',
    color: '#1e3a5f',
    activeColor: '#0E9ED5',
    hoverColor: '#38bdf8',
    description: 'Pan-European logistics network with strategic hubs and customs expertise for seamless cross-border operations.',
    countries: [
      { name: 'Albania', code: 'AL' },
      { name: 'Andorra', code: 'AD' },
      { name: 'Austria', code: 'AT' },
      { name: 'Belgium', code: 'BE', hasOffice: true, isPartner: true },
      { name: 'Bosnia & Herzegovina', code: 'BA' },
      { name: 'Bulgaria', code: 'BG' },
      { name: 'Croatia', code: 'HR' },
      { name: 'Czech Republic', code: 'CZ' },
      { name: 'Denmark', code: 'DK' },
      { name: 'Estonia', code: 'EE' },
      { name: 'Finland', code: 'FI' },
      { name: 'France', code: 'FR' },
      { name: 'Germany', code: 'DE', isPartner: true },
      { name: 'Greece', code: 'GR' },
      { name: 'Hungary', code: 'HU' },
      { name: 'Iceland', code: 'IS' },
      { name: 'Ireland', code: 'IE' },
      { name: 'Italy', code: 'ICT' },
      { name: 'Latvia', code: 'LV' },
      { name: 'Lithuania', code: 'LT' },
      { name: 'Luxembourg', code: 'LU' },
      { name: 'Malta', code: 'MT' },
      { name: 'Monaco', code: 'MC' },
      { name: 'Montenegro', code: 'ME' },
      { name: 'Netherlands', code: 'NL', isPartner: true },
      { name: 'Norway', code: 'NO' },
      { name: 'Poland', code: 'PL', isPartner: true },
      { name: 'Portugal', code: 'PT' },
      { name: 'Romania', code: 'RO' },
      { name: 'San Marino', code: 'SM' },
      { name: 'Scotland', code: 'GB-SCT' },
      { name: 'Serbia', code: 'RS' },
      { name: 'Slovakia', code: 'SK' },
      { name: 'Slovenia', code: 'SI' },
      { name: 'Spain', code: 'ES' },
      { name: 'Sweden', code: 'SE' },
      { name: 'Switzerland', code: 'CH', isPartner: true },
      { name: 'United Kingdom', code: 'GB', isPartner: true },
      { name: 'Vatican City', code: 'VA' },
    ],
    stats: { markets: 39, partners: 60 },
    services: ['ICT Asset Management', 'Freight Forwarding', 'Customs Clearance', 'IOR/EOR Solutions'],
  },
  {
    id: 'apac',
    name: 'APAC',
    color: '#1a3555',
    activeColor: '#0E9ED5',
    hoverColor: '#38bdf8',
    description: 'Comprehensive coverage across Asia-Pacific with headquarters in Singapore and offices throughout the region.',
    countries: [
      { name: 'Afghanistan', code: 'AF' },
      { name: 'Australia', code: 'AU', isPartner: true },
      { name: 'Bangladesh', code: 'BD' },
      { name: 'Bhutan', code: 'BT' },
      { name: 'Brunei', code: 'BN' },
      { name: 'Cambodia', code: 'KH' },
      { name: 'China', code: 'CN', isPartner: true },
      { name: 'Hong Kong', code: 'HK', isPartner: true },
      { name: 'India', code: 'IN', hasOffice: true, isPartner: true },
      { name: 'Indonesia', code: 'ID', isPartner: true },
      { name: 'Japan', code: 'JP', isPartner: true },
      { name: 'Kazakhstan', code: 'KZ' },
      { name: 'Kyrgyzstan', code: 'KG' },
      { name: 'Malaysia', code: 'MY', isPartner: true },
      { name: 'Maldives', code: 'MV' },
      { name: 'Mongolia', code: 'MN' },
      { name: 'Myanmar', code: 'MM' },
      { name: 'Nepal', code: 'NP' },
      { name: 'New Zealand', code: 'NZ', isPartner: true },
      { name: 'Pakistan', code: 'PK' },
      { name: 'Palau', code: 'PW' },
      { name: 'Philippines', code: 'PH', isPartner: true },
      { name: 'Singapore', code: 'SG', hasOffice: true, isPartner: true },
      { name: 'Solomon Islands', code: 'SB' },
      { name: 'South Korea', code: 'KR', isPartner: true },
      { name: 'Sri Lanka', code: 'LK', hasOffice: true, isPartner: true },
      { name: 'Taiwan', code: 'TW', isPartner: true },
      { name: 'Tajikistan', code: 'TJ' },
      { name: 'Thailand', code: 'TH', isPartner: true },
      { name: 'Turkmenistan', code: 'TM' },
      { name: 'Uzbekistan', code: 'UZ' },
      { name: 'Vanuatu', code: 'VU' },
      { name: 'Vietnam', code: 'VN', hasOffice: true, isPartner: true },
    ],
    stats: { markets: 33, partners: 85 },
    services: ['ICT Asset Management', 'Freight Forwarding', 'IOR/EOR Solutions', 'Customs Clearance'],
  },
  {
    id: 'africa',
    name: 'Africa',
    color: '#162d4a',
    activeColor: '#0E9ED5',
    hoverColor: '#38bdf8',
    description: 'Strategic logistics solutions across the African continent with local expertise and customs knowledge.',
    countries: [
      { name: 'Algeria', code: 'DZ' },
      { name: 'Angola', code: 'AO' },
      { name: 'Benin', code: 'BJ' },
      { name: 'Botswana', code: 'BW' },
      { name: 'Burkina Faso', code: 'BF' },
      { name: 'Cameroon', code: 'CM' },
      { name: 'Chad', code: 'TD' },
      { name: 'Congo', code: 'CG' },
      { name: 'DRC', code: 'CD' },
      { name: 'Egypt', code: 'EG' },
      { name: 'Eritrea', code: 'ER' },
      { name: 'Ethiopia', code: 'ET' },
      { name: 'Gabon', code: 'GA' },
      { name: 'Ghana', code: 'GH' },
      { name: 'Guinea', code: 'GN' },
      { name: 'Ivory Coast', code: 'CI' },
      { name: 'Kenya', code: 'KE' },
      { name: 'Lesotho', code: 'LS' },
      { name: 'Liberia', code: 'LR' },
      { name: 'Madagascar', code: 'MG' },
      { name: 'Malawi', code: 'MW' },
      { name: 'Mali', code: 'ML' },
      { name: 'Mauritania', code: 'MR' },
      { name: 'Morocco', code: 'MA' },
      { name: 'Mozambique', code: 'MZ' },
      { name: 'Namibia', code: 'NA' },
      { name: 'Niger', code: 'NE' },
      { name: 'Nigeria', code: 'NG' },
      { name: 'Senegal', code: 'SN' },
      { name: 'Sierra Leone', code: 'SL' },
      { name: 'Somalia', code: 'SO' },
      { name: 'South Africa', code: 'ZA', isPartner: true },
      { name: 'Sudan', code: 'SD' },
      { name: 'Swaziland', code: 'SZ' },
      { name: 'Tanzania', code: 'TZ' },
      { name: 'Togo', code: 'TG' },
      { name: 'Tunisia', code: 'TN' },
      { name: 'Uganda', code: 'UG' },
      { name: 'Western Sahara', code: 'EH' },
      { name: 'Zimbabwe', code: 'ZW' },
    ],
    stats: { markets: 40, partners: 25 },
    services: ['ICT Asset Management', 'Freight Forwarding', 'IOR/EOR Solutions'],
  },
  {
    id: 'americas',
    name: 'Americas',
    color: '#192f4d',
    activeColor: '#0E9ED5',
    hoverColor: '#38bdf8',
    description: 'Full coverage across the Americas with integrated cross-border solutions and local expertise.',
    countries: [
      { name: 'Argentina', code: 'AR' },
      { name: 'Bolivia', code: 'BO' },
      { name: 'Brazil', code: 'BR' },
      { name: 'Canada', code: 'CA', isPartner: true },
      { name: 'Chile', code: 'CL' },
      { name: 'Colombia', code: 'CO' },
      { name: 'Costa Rica', code: 'CR' },
      { name: 'Cuba', code: 'CU' },
      { name: 'Dominican Republic', code: 'DO' },
      { name: 'Ecuador', code: 'EC' },
      { name: 'Falkland Islands', code: 'FK' },
      { name: 'French Guiana', code: 'GF' },
      { name: 'Guatemala', code: 'GT' },
      { name: 'Guyana', code: 'GY' },
      { name: 'Haiti', code: 'HT' },
      { name: 'Honduras', code: 'HN' },
      { name: 'Jamaica', code: 'JM' },
      { name: 'Mexico', code: 'MX' },
      { name: 'Nicaragua', code: 'NI' },
      { name: 'Panama', code: 'PA' },
      { name: 'Peru', code: 'PE' },
      { name: 'Suriname', code: 'SR' },
      { name: 'Uruguay', code: 'UY' },
      { name: 'USA', code: 'US', isPartner: true },
      { name: 'Alaska', code: 'US' },
      { name: 'Antigua & Barbuda', code: 'AG' },
      { name: 'Bahamas', code: 'BS' },
      { name: 'Barbados', code: 'BB' },
      { name: 'Belize', code: 'BZ' },
      { name: 'Grenada', code: 'GD' },
      { name: 'Saint Kitts & Nevis', code: 'KN' },
      { name: 'Saint Lucia', code: 'LC' },
    ],
    stats: { markets: 32, partners: 35 },
    services: ['ICT Asset Management', 'Freight Forwarding', 'Customs Clearance'],
  },
  {
    id: 'middleEast',
    name: 'Middle East',
    color: '#1b3250',
    activeColor: '#0E9ED5',
    hoverColor: '#38bdf8',
    description: 'Strategic presence across the Middle East with local partners and expertise in regional trade regulations.',
    countries: [
      { name: 'Bahrain', code: 'BH' },
      { name: 'Cyprus', code: 'CY' },
      { name: 'Iran', code: 'IR' },
      { name: 'Iraq', code: 'IQ' },
      { name: 'Israel', code: 'IL', isPartner: true },
      { name: 'Jordan', code: 'JO' },
      { name: 'Kuwait', code: 'KW' },
      { name: 'Lebanon', code: 'LB' },
      { name: 'Oman', code: 'OM' },
      { name: 'Palestine', code: 'PS' },
      { name: 'Qatar', code: 'QA' },
      { name: 'Saudi Arabia', code: 'SA', isPartner: true },
      { name: 'Syria', code: 'SY' },
      { name: 'Turkey', code: 'TR', isPartner: true },
      { name: 'UAE', code: 'AE', hasOffice: true, isPartner: true },
    ],
    stats: { markets: 15, partners: 20 },
    services: ['ICT Asset Management', 'Freight Forwarding', 'IOR/EOR Solutions'],
  },
];

// Office locations (atlasName = the country's name in the world-atlas geometry)
const OFFICE_LOCATIONS = [
  // label = country name placed beside the dot (dx/dy in map units, picked so neighbouring labels do not collide)
  { name: 'Singapore', country: 'Singapore', atlasName: 'Singapore', type: 'HQ', coordinates: [103.8198, 1.3521] as [number, number], label: { dx: 13, dy: 4, anchor: 'start' as const } },
  { name: 'Antwerp', country: 'Belgium', atlasName: 'Belgium', type: 'Office', coordinates: [4.4025, 51.2194] as [number, number], label: { dx: -9, dy: 4, anchor: 'end' as const } },
  { name: 'Dubai', country: 'UAE', atlasName: 'United Arab Emirates', type: 'Office', coordinates: [55.2708, 25.2048] as [number, number], label: { dx: -9, dy: 4, anchor: 'end' as const } },
  { name: 'Ho Chi Minh City', country: 'Vietnam', atlasName: 'Vietnam', type: 'Office', coordinates: [106.6297, 10.8231] as [number, number], label: { dx: 9, dy: -3, anchor: 'start' as const } },
  { name: 'Chennai', country: 'India', atlasName: 'India', type: 'Office', coordinates: [80.2707, 13.0827] as [number, number], label: { dx: -9, dy: -2, anchor: 'end' as const } },
  { name: 'Colombo', country: 'Sri Lanka', atlasName: 'Sri Lanka', type: 'Office', coordinates: [79.8612, 6.9271] as [number, number], label: { dx: -9, dy: 9, anchor: 'end' as const } },
  { name: 'Miami', country: 'USA', atlasName: 'United States of America', type: 'Opening', coordinates: [-80.1918, 25.7617] as [number, number], label: { dx: -9, dy: 4, anchor: 'end' as const } },
];

interface InteractiveWorldMapProps {
  onRegionSelect?: (region: Region | null) => void;
  onCountryClick?: (countryCode: string) => void;
  selectedRegion?: Region | null;
  showOfficeMarkers?: boolean;
  className?: string;
}

const InteractiveWorldMap: React.FC<InteractiveWorldMapProps> = ({
  onRegionSelect,
  onCountryClick,
  selectedRegion,
  showOfficeMarkers = true,
  className = '',
}) => {
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const getRegionForCountry = (countryName: string): Region | undefined => {
    const regionId = COUNTRY_REGIONS[countryName];
    return REGIONS.find(r => r.id === regionId);
  };

  const getCountryColor = (countryName: string): string => {
    const region = getRegionForCountry(countryName);
    if (!region) return UNSERVED_FILL;

    if (hoveredCountry === countryName) return TRUELOG_CYAN;
    if (selectedRegion?.id === region.id) return REGION_SELECTED_FILL;
    if (hoveredRegion === region.id) return REGION_HOVER_FILL;
    return SERVED_FILL;
  };

  const getCountryStroke = (countryName: string): string => {
    if (hoveredCountry === countryName) return '#7dd3fc';
    return getRegionForCountry(countryName) ? SERVED_STROKE : '#1e293b';
  };

  const getCountryStrokeWidth = (countryName: string): number => {
    return hoveredCountry === countryName ? 1.2 : 0.4;
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleCountryClick = (countryName: string) => {
    const region = getRegionForCountry(countryName);
    if (region) {
      const country = region.countries.find(c => c.name === countryName);
      if (country && onCountryClick) {
        onCountryClick(country.code);
        return;
      }
      if (onRegionSelect) {
        if (selectedRegion?.id === region.id) {
          onRegionSelect(null);
        } else {
          onRegionSelect(region);
        }
      }
    }
  };

  const hoveredRegionData = useMemo(() => {
    if (hoveredCountry) return getRegionForCountry(hoveredCountry);
    return null;
  }, [hoveredCountry]);

  const hoveredOffices = useMemo(
    () => OFFICE_LOCATIONS.filter((office) => office.atlasName === hoveredCountry),
    [hoveredCountry]
  );

  return (
    <div className={`relative ${className}`} onMouseMove={handleMouseMove}>
      {/* Region selector pills sit above the map so they never cover land */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {REGIONS.map((region) => (
          <button
            key={`${region.id}-top`}
            onClick={() => onRegionSelect?.(selectedRegion?.id === region.id ? null : region)}
            className={`px-3 py-1 rounded-full text-[11px] font-medium transition-all duration-200 border ${
              selectedRegion?.id === region.id
                ? 'bg-[#0E9ED5] border-[#0E9ED5] text-white shadow-lg shadow-cyan-500/20'
                : 'bg-white border-slate-200 text-slate-600 hover:border-[#0E9ED5]/50 hover:text-slate-900'
            }`}
          >
            {region.name}
            <span className="ml-1.5 text-[10px] opacity-60">{region.stats.markets}</span>
          </button>
        ))}
      </div>

      {/* Map container with grid background; 2:1 matches the 800x400 map so the world fills it */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/40 border border-white/10 aspect-[2/1]"
        style={{ background: 'linear-gradient(135deg, #020617 0%, #0a1628 50%, #020617 100%)' }}>

        {/* Subtle grid overlay */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{ backgroundImage: 'linear-gradient(rgba(14,158,213,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(14,158,213,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

        {/* Glow effects */}
        <div className="absolute -top-20 left-1/4 w-96 h-96 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${TRUELOG_CYAN}15, transparent 70%)` }} />
        <div className="absolute -bottom-20 right-1/4 w-80 h-80 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${TRUELOG_BLUE}12, transparent 70%)` }} />

        <ComposableMap
          width={800}
          height={400}
          projectionConfig={{ scale: 147, center: [10, 12] }}
          className="absolute inset-0 h-full w-full"
          style={{ width: '100%', height: '100%' }}
        >
          <ZoomableGroup zoom={1}>
            <Geographies geography="https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json">
              {({ geographies }: { geographies: any[] }) =>
                geographies.filter((geo: any) => !HIDDEN_GEOGRAPHIES.has(geo.properties.name)).map((geo: any) => {
                  const countryName = geo.properties.name;
                  const region = getRegionForCountry(countryName);

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getCountryColor(countryName)}
                      stroke={getCountryStroke(countryName)}
                      strokeWidth={getCountryStrokeWidth(countryName)}
                      onMouseEnter={() => {
                        setHoveredCountry(countryName);
                        if (region) setHoveredRegion(region.id);
                      }}
                      onMouseLeave={() => {
                        setHoveredCountry(null);
                        setHoveredRegion(null);
                      }}
                      onClick={() => handleCountryClick(countryName)}
                      style={{
                        default: { outline: 'none', cursor: region ? 'pointer' : 'default', transition: 'fill 0.3s ease, stroke 0.3s ease' },
                        hover: { outline: 'none', cursor: region ? 'pointer' : 'default' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {/* Office Markers */}
            {showOfficeMarkers && OFFICE_LOCATIONS.map((office, index) => (
              <Marker key={office.name} coordinates={office.coordinates}>
                <motion.g
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.08 }}
                  className="cursor-pointer"
                >
                  {office.type === 'HQ' ? (
                    <>
                      <motion.circle r={14} fill="none" stroke={TRUELOG_CYAN} strokeWidth={1}
                        initial={{ scale: 1, opacity: 0.5 }}
                        animate={{ scale: 1.8, opacity: 0 }}
                        transition={{ duration: 2.5, repeat: Infinity }} />
                      <circle r={9} fill={TRUELOG_CYAN} stroke="#fff" strokeWidth={2.5} opacity={0.95} />
                      <circle r={3.5} fill="#fff" />
                    </>
                  ) : office.type === 'Opening' ? (
                    <>
                      <motion.circle r={10} fill="none" stroke={TRUELOG_ORANGE} strokeWidth={0.8}
                        initial={{ scale: 1, opacity: 0.4 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }} />
                      <circle r={5.5} fill={TRUELOG_ORANGE} stroke="#fff" strokeWidth={1.5} opacity={0.9} />
                    </>
                  ) : (
                    <>
                      <motion.circle r={10} fill="none" stroke={TRUELOG_BLUE} strokeWidth={0.8}
                        initial={{ scale: 1, opacity: 0.4 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }} />
                      <circle r={5.5} fill={TRUELOG_BLUE} stroke="#fff" strokeWidth={1.5} opacity={0.9} />
                    </>
                  )}
                  <text
                    x={office.label.dx}
                    y={office.label.dy}
                    textAnchor={office.label.anchor}
                    fontSize={10}
                    fontWeight={600}
                    fill="#e6f0f8"
                    stroke="#06182b"
                    strokeWidth={3}
                    paintOrder="stroke"
                    style={{ letterSpacing: '0.04em', pointerEvents: 'none' }}
                  >
                    {office.country}
                  </text>
                </motion.g>
              </Marker>
            ))}
          </ZoomableGroup>
        </ComposableMap>
      </div>

      {/* Hover Tooltip - Dark theme */}
      <AnimatePresence>
        {hoveredCountry && hoveredRegionData && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="fixed z-50 pointer-events-none"
            style={{ left: tooltipPosition.x + 16, top: tooltipPosition.y + 16 }}
          >
            <div className="bg-slate-900/95 backdrop-blur-xl rounded-xl border border-white/15 shadow-2xl shadow-black/50 px-4 py-3 min-w-[180px]">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: TRUELOG_CYAN }} />
                <span className="text-sm font-semibold text-white">{hoveredCountry}</span>
              </div>
              <div className="text-xs text-slate-400">{hoveredRegionData.name}</div>
              <div className="mt-2 pt-2 border-t border-white/10 text-xs text-slate-300">
                {hoveredOffices.length > 0
                  ? `TrueLog office: ${hoveredOffices.map((office) => (office.type === 'Opening' ? `${office.name} (opening 2027)` : office.name)).join(', ')}`
                  : 'Served through the TrueLog network'}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legend - one set of keys, below the map */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full border-2 border-white shadow" style={{ backgroundColor: TRUELOG_CYAN }} />
          <span>Headquarters (Singapore)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full border border-white shadow" style={{ backgroundColor: TRUELOG_BLUE }} />
          <span>TrueLog office</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full border border-white shadow" style={{ backgroundColor: TRUELOG_ORANGE }} />
          <span>2027 opening (Miami)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: SERVED_FILL, border: `1px solid ${SERVED_STROKE}` }} />
          <span>Country we serve</span>
        </div>
      </div>

    </div>
  );
};

export default InteractiveWorldMap;
export { COUNTRY_REGIONS, OFFICE_LOCATIONS };
